// Shared AG Charts options for AABI (§9.4): one typography, currency
// formatting, grid-line style and palette extracted from the SalesChart
// pattern so AABI charts stay consistent without copying objects.

export const AABI_CHART_FONT =
  "Figtree, system-ui, -apple-system, BlinkMacSystemFont, sans-serif";

export const AABI_CHART_COLORS = {
  primary: "#2663EB",
  muted: "#C7D7F8",
  gridLine: "#E5E7EB",
  text: "#767676",
};

// Slice palette for the portfolio donut cascade. Built entirely from the two
// brand hues in theme/colors.js — primaryColor (#2663EB) and secondaryColor
// (#1DACFF) — as alternating shades and tints, so charts read as part of the
// product rather than as a second, unrelated colour system. Alternating
// between the two hues keeps adjacent slices distinguishable while the
// lightening ramp keeps a long series legible.
export const AABI_DONUT_PALETTE = [
  "#2663EB", // primaryColor
  "#1DACFF", // secondaryColor
  "#1B50C8", // primaryColorDark
  "#6C9BFF", // primary tint
  "#0E86CC", // secondary shade
  "#A8C6FF", // primary tint, lighter
  "#123A94", // primary, deepest
  "#7FD3FF", // secondary tint
  "#4F7DE8", // primary mid
  "#C9E7FA", // secondary tint, lightest
];

// Receivables aging (Executive Dashboard §5.5): a severity ramp from the
// primary blue (not yet due) through warning to failure shades. The warning and
// failure steps are darker shades of the status tokens so every slice keeps at
// least 3:1 against the white panel; the legend always names each bucket, so
// colour is never the only cue.
export const AGING_SEVERITY_PALETTE = {
  current: "#2663EB", // primaryColor
  "1_30": "#B87A00", // warningColor, darkened
  "31_60": "#D9500B", // warningColor to failureColor, mid step
  "61_90": "#C62828", // failureColor, darkened
  "90_plus": "#7F1010", // failureColorDark, deepened
  no_due_date: "#767676", // lightText: a data-quality bucket, deliberately neutral
};

// Revenue composition (§5.4): Realized, Pending invoices, Uninvoiced orders.
export const COMPOSITION_PALETTE = {
  realized: "#2663EB", // primaryColor
  pending: "#0E86CC", // secondary shade
  uninvoiced: "#8FB0F5", // primary tint
};

// Billed vs collected (§5.4).
export const CASH_PALETTE = {
  billed: "#2663EB",
  collected: "#0E86CC",
};

// Canvas text can't take a CSS class, so this mirrors .text-numberBig
// (common/typography/styles.css) for the donut centre figure.
export const AABI_NUMBER_BIG_LABEL = {
  fontFamily: AABI_CHART_FONT,
  fontSize: 24,
  fontWeight: 600,
};

// The base options every AABI chart spreads. Respect reduced motion (§9.4):
// callers disable animation when the user prefers reduced motion. Axes are
// chart-specific (horizontal bars vs time series) and defined by each chart.
export const getAabiChartBaseOptions = ({ reducedMotion = false } = {}) => ({
  padding: { top: 16, right: 16, bottom: 16, left: 0 },
  animation: { enabled: !reducedMotion },
  theme: {
    baseTheme: "ag-default",
    overrides: {
      common: {
        fontFamily: AABI_CHART_FONT,
        fontSize: 12,
      },
    },
  },
});

export const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;
